// Cutoff / heard-text rendering.
//
// The backend estimates how much of a bot turn the caller actually heard before
// barging in (`heard_estimate.go`) and stores it as `heardText` alongside the
// turn's full `text`. Every renderer shows `heardText` solid and the remainder
// faded, which is only meaningful if heardText is a PREFIX of text.
//
// That invariant is not enforced on the write side: `UpdateLastBotHeardText`
// (call_recorder.go) annotates the newest bot entry positionally, so when a newer
// turn lands between the estimate and the annotation the two belong to DIFFERENT
// utterances. Slicing by a foreign length then deletes the first N characters of a
// perfectly good sentence — observed in prod 2026-08-31, where a bot-handover
// announcement's heardText was stamped onto the target bot's first response and
// the call log rendered "…ion update, I'm going to try…" (65 chars destroyed,
// cut mid-word at "verificat|ion").
//
// cutoffSplit fails OPEN: when the pairing is invalid it returns null and the
// caller renders `text` intact and unstyled. Losing the ✂ marker on a corrupt row
// beats losing the caller's actual words.
export interface CutoffSplit {
  /** The prefix the caller is estimated to have heard (may be empty). */
  heard: string;
  /** The remainder to render faded. */
  rest: string;
}

/**
 * Returns the heard/unheard split for a cut-off turn, or null when the turn
 * should be rendered as plain text.
 *
 * Null is returned when:
 *  - either side is missing;
 *  - heardText is not shorter than text (fully heard → not a cutoff);
 *  - heardText is not a prefix of text (mismatched pairing → fail open).
 *
 * An empty heardText IS a valid split (the caller barged in before any audio
 * played): it yields the whole text faded, preserving existing behaviour.
 */
export function cutoffSplit(text?: string, heardText?: string): CutoffSplit | null {
  if (text === undefined || heardText === undefined) return null;
  if (heardText.length >= text.length) return null;
  if (!text.startsWith(heardText)) return null;
  return { heard: heardText, rest: text.slice(heardText.length) };
}
